// src/pages/CreatorOnboard.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Backdrop,
  CircularProgress, Box, TextField, Divider, MenuItem, FormControlLabel, Checkbox, Stack
} from '@mui/material';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { sha256 } from 'js-sha256';

import routes from '../Config/routes';
import client from '../API';
import { niches, region } from '../Utils/constants';
import MuiPhoneInput from '../Components/MuiPhoneInput';
import AlertDialog from '../Components/AlertDialog';
import useAlertDialog from '../Components/useAlertDialog';

/* -------------------------------------------------------------------------- */
/*  Google OAuth constants                                                    */
/* -------------------------------------------------------------------------- */
const GOOGLE_IOS_CLIENT_ID =
  '489265257959-374fsc04dffos29tto4jsqcd2sch5f9o.apps.googleusercontent.com';
const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/gmail.modify',
];
const REDIRECT_URI =
  'com.googleusercontent.apps.489265257959-374fsc04dffos29tto4jsqcd2sch5f9o:/oauth2redirect';

/* -------------------------------------------------------------------------- */
/*  PKCE helpers (same helpers you already use in UnifiedLogin)               */
/* -------------------------------------------------------------------------- */
const base64url = (uint8) =>
  btoa(String.fromCharCode(...uint8))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const createPKCE = () => {
  const rnd = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64url(rnd);
  const hash = sha256.arrayBuffer(verifier);
  const challenge = base64url(new Uint8Array(hash));
  return { verifier, challenge };
};

const exchangeCodeForIdToken = async (code, codeVerifier) => {
  const body = new URLSearchParams({
    code,
    client_id: GOOGLE_IOS_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    grant_type: 'authorization_code',
    code_verifier: codeVerifier,
  }).toString();

  const res = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error_description || 'Google token exchange failed');
  }
  const data = await res.json();
  if (!data.id_token) throw new Error('No id_token returned from Google');
  return data.id_token;
};

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */
const CreatorOnboard = ({ conversationIdParam, inviteEmailParam, prefilledUsername = '' }) => {
  const navigate = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  /* ------------------------------ state ----------------------------------- */
  const [username, setUsername] = useState(prefilledUsername);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [creatorRegion, setCreatorRegion] = useState('');
  const [creatorNiche, setCreatorNiche] = useState([]);
  const [platformHandles, setPlatformHandles] = useState({
    instagram: '', tiktok: '', youtube: '', podcast: '', twitch: '', kick: '', x: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const pkceRef = useRef(null);
  const tabOpen = useRef(false);
  const isWeb = Capacitor.getPlatform() === 'web';

  /* ----------------------- helpers / UI feedback -------------------------- */
  const alertErr = (msg) => openDialog('Error', msg, closeDialog, closeDialog, 'Ok', null);

  const onSubmitLocalValidation = () => {
    if (!termsAccepted) return alertErr('You must accept the Terms & Conditions to continue.');
    if (username.includes(' ')) return setValidationError('Creator name cannot contain spaces.');
    if (phoneNumber.length < 9) return alertErr('You must enter a valid phone number.');
    const anyHandle = Object.values(platformHandles).some((v) => v);
    if (!anyHandle) return alertErr('Enter at least one platform username.');
    return true;
  };

  /* ------------------------------ API call -------------------------------- */
  const submitCreator = async (googleToken) => {
    if (!onSubmitLocalValidation()) return;
    setLoading(true);

    const payload = {
      username,
      is_partner: false,
      region: creatorRegion,
      niche: creatorNiche,
      google_code: googleToken,              // <-- backend accepts this field
      phone_number: phoneNumber,
      youtube: platformHandles.youtube ? `https://www.youtube.com/@${platformHandles.youtube}` : null,
      instagram: platformHandles.instagram ? `https://www.instagram.com/${platformHandles.instagram}` : null,
      tiktok: platformHandles.tiktok ? `https://www.tiktok.com/@${platformHandles.tiktok}` : null,
      podcast: platformHandles.podcast || null,
      x: platformHandles.x ? `https://www.x.com/${platformHandles.x}` : null,
      twitch: platformHandles.twitch ? `https://www.twitch.tv/${platformHandles.twitch}` : null,
      kick: platformHandles.kick ? `https://www.kick.com/${platformHandles.kick}` : null,
    };

    try {
      await client.creatorConnect.onboard(payload);
      openDialog(
        'Success',
        'You have successfully created your CreatorConnect account.',
        () => navigate(`${routes.login}?tab=creator`)
      );
    } catch (err) {
      console.error(err);
      alertErr(`There was an error creating your account: ${err.response?.data?.error || err.message}`);
      setLoading(false);
    }
  };

  /* --------------------------- Web Google flow --------------------------- */
  const webGoogleSuccess = (resp) => submitCreator(resp?.code || resp?.credential);
  const webGoogleFail = (e) => alertErr(e.error ?? 'Google sign-in failed. Please try again.');

  /* --------------------------- Native Google flow ------------------------ */
  const nativeGoogle = async () => {
    try {
      pkceRef.current = createPKCE();
      const { challenge } = pkceRef.current;

      tabOpen.current = true;
      setLoading(true);

      await Browser.open({
        url: `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(
          GOOGLE_IOS_CLIENT_ID
        )}&redirect_uri=${encodeURIComponent(
          REDIRECT_URI
        )}&response_type=code&code_challenge=${encodeURIComponent(
          challenge
        )}&code_challenge_method=S256&scope=${encodeURIComponent(GOOGLE_SCOPES.join(' '))}&prompt=select_account`,
        presentationStyle: 'fullscreen',
      });
    } catch (err) {
      console.error('[CreatorOnboard] Native Google launch error:', err);
      alertErr(err.message || 'Google login cancelled');
      setLoading(false);
      tabOpen.current = false;
    }
  };

  /* ---------- Deep-link handler (only active inside the iOS app) ---------- */
  const handleDeepLink = useCallback(
    async ({ url }) => {
      try {
        const u = new URL(url);
        if (
          u.protocol.startsWith('com.googleusercontent.apps.') &&
          u.pathname === '/oauth2redirect'
        ) {
          if (tabOpen.current) {
            await Browser.close().catch(() => {});
            tabOpen.current = false;
          }

          const code = u.searchParams.get('code');
          const error = u.searchParams.get('error');
          if (error) throw new Error(`Google OAuth error: ${error}`);
          if (!code) throw new Error('Missing OAuth code');

          const idToken = await exchangeCodeForIdToken(code, pkceRef.current?.verifier);
          await submitCreator(idToken);            // continue onboarding
        }
      } catch (err) {
        console.error('[CreatorOnboard] Deep link error:', err);
        alertErr(err.message || 'Google login failed');
        setLoading(false);
      }
    },
    [] 
  );

  useEffect(() => {
    if (!isWeb) {
      const urlSub = App.addListener('appUrlOpen', handleDeepLink);
      const browserSub = Browser.addListener('browserFinished', () => {
        tabOpen.current = false;
        setLoading(false);
      });
      return () => {
        urlSub.remove();
        browserSub.remove();
      };
    }
  }, [isWeb, handleDeepLink]);

  /* ------------------------------ UI ------------------------------------- */
  const handleChange = (field) => (e) =>
    setPlatformHandles((s) => ({ ...s, [field]: e.target.value }));

  return (
    <Box sx={{ p: 2 }}>
      <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <AlertDialog alertState={dialogState} />

      {/* optional disclaimer modal */}
      <Dialog open={disclaimerOpen} onClose={() => setDisclaimerOpen(false)}>
        <DialogTitle>Signup Info</DialogTitle>
        <DialogContent>
          <Typography>
            This signup form creates your Blitz CreatorConnect profile. We secure your account
            with Google OAuth to keep your data safe.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisclaimerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------- FORM ------------------------- */}
      <form onSubmit={(e) => e.preventDefault()} noValidate>
        <Stack spacing={2} sx={{ maxWidth: 480, mx: 'auto', mt: 4 }}>
          <Typography variant="h3" align="center">
            Account Setup
          </Typography>

          <TextField
            label="Username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!validationError}
            helperText={validationError || 'Use your main platform handle'}
          />

          <TextField
            label="Region"
            select
            required
            value={creatorRegion}
            onChange={(e) => setCreatorRegion(e.target.value)}
          >
            {region.map((r) => (
              <MenuItem key={r} value={r}>
                {r === 'NACAUKAUS' ? 'USA, Canada, UK, Australia' : r}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Niche"
            select
            required
            value={creatorNiche}
            SelectProps={{ multiple: true }}
            onChange={(e) => setCreatorNiche(e.target.value)}
          >
            {niches.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <MuiPhoneInput
            country="us"
            preferredCountries={['us']}
            value={phoneNumber}
            onChange={setPhoneNumber}
            required
          />

          <Divider />

          {[
            ['youtube', '@YourChannelName'],
            ['instagram'],
            ['tiktok', '@yourusername'],
            ['podcast'],
            ['x'],
            ['kick'],
            ['twitch'],
          ].map(([field, hint]) => (
            <TextField
              key={field}
              label={`${field.charAt(0).toUpperCase() + field.slice(1)} Username`}
              value={platformHandles[field]}
              helperText={hint}
              onChange={handleChange(field)}
            />
          ))}

          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I agree to the&nbsp;
                <a href="/terms_creators" target="_blank" rel="noopener noreferrer">
                  Terms &amp; Conditions
                </a>
              </Typography>
            }
          />

          {/* ------------------- Google Sign-in CTA ------------------- */}
          {isWeb ? (
            <GoogleLogin
              size="large"
              ux_mode="popup"
              flow="auth-code"
              access_type="offline"
              scope={GOOGLE_SCOPES.join(' ')}
              prompt="consent"
              onSuccess={webGoogleSuccess}
              onError={webGoogleFail}
              disabled={!termsAccepted}
            />
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={nativeGoogle}
              disabled={!termsAccepted || loading}
              sx={{ py: 1.5 }}
            >
              Sign up with Google
            </Button>
          )}
        </Stack>
      </form>
    </Box>
  );
};

export default CreatorOnboard;
